import type { AccountStatus } from '../auth/account-status.js';

export type WorkspaceWriteAccess = 'allowed' | 'choose_workspace' | 'unverified' | 'upgrade' | 'read_only' | 'scope_required';
/** UI affordances reflect server facts. The backend must still authorize each write. */
export function workspaceWriteAccess(status: AccountStatus | null | undefined, workspaceId: string | null, canWrite: boolean | undefined): WorkspaceWriteAccess {
  if (!workspaceId) return 'choose_workspace';
  if (!status) return 'unverified';
  if (status.entitled !== true || status.access_policy !== 'paid') return 'upgrade';
  if (canWrite !== true) return 'read_only';
  if (!status.effective_scopes.includes('workspace.write')) return 'scope_required';
  return 'allowed';
}
export const WORKSPACE_ACCESS_MESSAGES: Record<WorkspaceWriteAccess, string> = {
  allowed: 'Workspace changes are enabled. Each action is checked by the server.',
  choose_workspace: 'Choose a workspace to save or follow legislation.',
  unverified: 'Account access is not yet verified. Refresh account status before making changes.',
  upgrade: 'Your account includes personal tracking. This shared workspace action requires a paid plan.',
  read_only: 'You have read-only access to this workspace.',
  scope_required: 'This connection does not have the required write permission.',
};
export function accountUsageLabel(status: AccountStatus): string {
  const { used, limit, period } = status.quota;
  const count = (value: number | null) => value === null ? 'not reported' : value.toLocaleString('en-US');
  if (used === null && limit === null) return 'Server request usage not reported';
  return `${count(used)} / ${count(limit)} server requests${period ? ` this ${period}` : ''}`;
}

/** Explorer personal follows use the server's bounded tracking policy. */
export function legislationFollowAccess(status: AccountStatus | null | undefined, workspaceId: string | null, canWrite: boolean | undefined, policy: { personal_workspace: boolean; explorer: boolean; active_follows: number; follow_limit: number | null } | null | undefined, following = false): WorkspaceWriteAccess | 'limit_reached' {
  const shared = workspaceWriteAccess(status, workspaceId, canWrite);
  if (shared !== 'upgrade' || !status || status.access_policy !== 'personal') return shared;
  if (canWrite !== true) return 'read_only';
  if (!status.effective_scopes.includes('personal.write')) return 'scope_required';
  if (!policy || !Number.isSafeInteger(policy.active_follows) || policy.active_follows < 0 || typeof policy.follow_limit !== 'number' || !Number.isSafeInteger(policy.follow_limit) || policy.follow_limit < 0) return 'unverified';
  if (!policy.personal_workspace) return 'upgrade';
  if (!policy.explorer) return 'unverified';
  return !following && policy.active_follows >= policy.follow_limit ? 'limit_reached' : 'allowed';
}
